// src/components/HouseholdNameModal.tsx
//
// BekalNanti.ID — Household Identity / Planning Space Modal
// Allows the user to independently manage the name of their family planning space.

import React, { useState, useEffect } from 'react'
import { updateHouseholdName } from '../lib/peopleService'
import { X, Users, Check, AlertCircle, Loader2 } from 'lucide-react'

interface HouseholdNameModalProps {
  isOpen: boolean
  householdId: string
  currentName: string
  onClose: () => void
  onUpdateSuccess: (newName: string) => void
}

export const HouseholdNameModal: React.FC<HouseholdNameModalProps> = ({
  isOpen,
  householdId,
  currentName,
  onClose,
  onUpdateSuccess,
}) => {
  const [name, setName] = useState(currentName || '')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    setName(currentName || '')
    setErrorMsg(null)
    setSuccessMsg(null)
  }, [currentName, isOpen])

  if (!isOpen) return null

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setErrorMsg('Nama ruang perencanaan tidak boleh kosong.')
      return
    }
    if (trimmed.length < 2) {
      setErrorMsg('Nama ruang perencanaan minimal 2 karakter.')
      return
    }
    if (trimmed.length > 60) {
      setErrorMsg('Nama ruang perencanaan maksimal 60 karakter.')
      return
    }

    setLoading(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    try {
      const updated = await updateHouseholdName(householdId, trimmed)
      setSuccessMsg('Nama ruang perencanaan berhasil disimpan!')
      setTimeout(() => {
        onUpdateSuccess(updated)
        onClose()
      }, 350)
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal menyimpan nama ruang perencanaan.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="household-modal-title"
    >
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200/90 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center">
              <Users size={18} />
            </div>
            <div>
              <h2 id="household-modal-title" className="text-base font-bold text-slate-900 leading-tight">
                Ubah Nama Ruang Perencanaan
              </h2>
              <p className="text-[11px] text-slate-500">
                Nama identitas keluarga atau ruang perencanaan masa depan Anda
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
            aria-label="Tutup"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} className="p-5 space-y-4 text-left">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
              <AlertCircle size={14} className="flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
          {successMsg && (
            <div className="p-3 rounded-xl bg-teal-50 border border-teal-200 text-[#0F766E] text-xs flex items-center gap-2">
              <Check size={14} className="flex-shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="household-name-input" className="text-xs font-bold text-slate-700">
              Nama Ruang Perencanaan
            </label>
            <input
              id="household-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Contoh: Keluarga Faezal, Keluarga Harmonis"
              maxLength={60}
              required
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
            />
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Nama ini akan muncul pada kartu Konteks Perencanaan di Beranda. Anda dapat mengubahnya kapan saja secara mandiri.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-semibold cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-2xs transition-all cursor-pointer disabled:opacity-50"
            >
              {loading && <Loader2 size={13} className="animate-spin" />}
              <span>Simpan Nama</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
